import { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";

interface VendorField {
  key: string;
  label: string;
  type: "text" | "password";
  required: boolean;
  placeholder?: string;
  helpText?: string;
}

interface VendorSetupStep {
  title: string;
  details?: string[];
}

interface VendorSetupInstructions {
  intro?: string;
  steps: VendorSetupStep[];
}

interface VendorDefinition {
  id: string;
  name: string;
  description: string;
  fields: VendorField[];
  setupInstructions?: VendorSetupInstructions;
}

interface Integration {
  id: string;
  vendor: string;
  enabled: boolean;
  credentials: Record<string, string>;
  config: Record<string, unknown>;
  last_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

const MASKED_VALUE = "***masked***";

const IntegrationManager = () => {
  const [vendors, setVendors] = useState<VendorDefinition[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [clearedFields, setClearedFields] = useState<Set<string>>(new Set());
  const [testResult, setTestResult] = useState<{
    success: boolean;
    error?: string;
  } | null>(null);

  const markSavedRef = useRef<() => void>(() => {});
  const { markSaved } = useUnsavedChanges({
    currentState: { credentials, enabled, selectedVendor },
    initialState: { credentials, enabled, selectedVendor },
    onCancel: () => {},
  });
  markSavedRef.current = markSaved;

  const fetchData = async () => {
    try {
      const [vendorRes, integrationRes] = await Promise.all([
        axios.get("/api/agency/vendors"),
        axios.get("/api/agency/integrations"),
      ]);
      setVendors(vendorRes.data);
      const integrationData = integrationRes.data as Integration[];
      setIntegrations(integrationData);

      // Auto-select if agency already has a configured integration
      if (!selectedVendor && integrationData.length > 0) {
        const first = integrationData[0];
        setSelectedVendor(first.vendor);
        setCredentials(first.credentials);
        setEnabled(first.enabled);
        setShowInstructions(false);
      } else if (selectedVendor) {
        const existing = integrationData.find(
          (i) => i.vendor === selectedVendor,
        );
        if (existing) {
          setCredentials(existing.credentials);
          setEnabled(existing.enabled);
        }
      }
    } catch (error) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
      // Reset unsaved changes snapshot after loading server data
      setTimeout(() => markSavedRef.current(), 0);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleVendorSelect = (vendorId: string) => {
    setSelectedVendor(vendorId);
    setConfirmDelete(false);
    setClearedFields(new Set());
    setTestResult(null);

    const existing = integrations.find((i) => i.vendor === vendorId);
    if (existing) {
      setCredentials(existing.credentials);
      setEnabled(existing.enabled);
    } else {
      setCredentials({});
      setEnabled(false);
    }
    setShowInstructions(!existing);
    setTimeout(() => markSavedRef.current(), 0);
  };

  const selectedVendorDef = vendors.find((v) => v.id === selectedVendor);
  const existingIntegration = integrations.find(
    (i) => i.vendor === selectedVendor,
  );

  const updateField = (fieldKey: string, value: string) => {
    setCredentials((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const clearMaskedField = (fieldKey: string) => {
    setClearedFields((prev) => {
      const next = new Set(prev);
      next.add(fieldKey);
      return next;
    });
    updateField(fieldKey, "");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post("/api/agency/integrations", {
        vendor: selectedVendor,
        credentials,
        enabled,
      });
      toast.success("Integration saved");
      setClearedFields(new Set());
      await fetchData();
      setTimeout(() => markSavedRef.current(), 0);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Failed to save integration",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setTestResult(null);
    try {
      await axios.post(`/api/agency/integrations/${selectedVendor}/verify`, {
        credentials,
      });
      setTestResult({ success: true });
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || "Verification failed",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/agency/integrations/${selectedVendor}`);
      toast.success("Integration removed");
      setConfirmDelete(false);
      setCredentials({});
      setEnabled(false);
      setClearedFields(new Set());
      await fetchData();
      setTimeout(() => markSavedRef.current(), 0);
    } catch (error) {
      toast.error("Failed to remove integration");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      <h2 className="text-2xl font-semibold text-base-content mb-2">
        Evidence & Video Integrations
      </h2>
      <p className="text-base-content/60 mb-6">
        Connect your agency's bodycam and evidence management system.
        Credentials are encrypted at rest.
      </p>

      <div className="space-y-6">
        {/* Vendor Selection */}
        <div>
          <label className="text-gray-800 dark:text-base-content/80 font-medium text-xs md:text-base pb-3 block">
            Choose Your Evidence Vendor
          </label>
          <div className="grid grid-cols-2 gap-4">
            {vendors.map((vendor) => {
              const isConfigured = integrations.some(
                (i) => i.vendor === vendor.id,
              );
              return (
                <div
                  key={vendor.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedVendor === vendor.id
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
                      : "border-base-300 hover:border-gray-300 dark:hover:border-base-content/30"
                  }`}
                  onClick={() => handleVendorSelect(vendor.id)}
                >
                  <h3 className="font-medium text-base-content">{vendor.name}</h3>
                  <span className="text-xs text-base-content/60">
                    {isConfigured ? "Configured" : vendor.description}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Setup Instructions */}
        {selectedVendorDef?.setupInstructions && (
          <div className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 rounded-lg">
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <span className="font-medium text-blue-900 dark:text-blue-200">
                How to set up {selectedVendorDef.name}
              </span>
              <span className="text-sm text-blue-700 dark:text-blue-300 underline">
                {showInstructions ? "Hide instructions" : "Show instructions"}
              </span>
            </button>
            {showInstructions && (
              <div className="px-4 pb-4 space-y-4">
                {selectedVendorDef.setupInstructions.intro && (
                  <p className="text-sm text-blue-900 dark:text-blue-200">
                    {selectedVendorDef.setupInstructions.intro}
                  </p>
                )}
                <ol className="space-y-3">
                  {selectedVendorDef.setupInstructions.steps.map(
                    (step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-base-content">
                            {step.title}
                          </p>
                          {step.details && (
                            <ul className="mt-1 space-y-1 list-disc list-outside ml-4">
                              {step.details.map((detail, detailIndex) => (
                                <li
                                  key={detailIndex}
                                  className="text-sm text-gray-700 dark:text-base-content/80"
                                >
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </li>
                    ),
                  )}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Credential Fields */}
        {selectedVendorDef && (
          <div className="space-y-6 p-6 bg-base-200 rounded-lg">
            <h3 className="text-lg font-medium text-base-content">
              {selectedVendorDef.name} Configuration
            </h3>

            {selectedVendorDef.fields.map((field) => {
              const value = credentials[field.key] || "";
              const isMasked =
                value === MASKED_VALUE && !clearedFields.has(field.key);

              return (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  {isMasked ? (
                    <div className="space-y-2">
                      <div className="w-full px-3 py-2 border border-green-300 dark:border-green-900 rounded-md bg-green-50 dark:bg-green-950/40 text-green-800 dark:text-green-400 text-sm">
                        &#10003; {field.label} is configured and saved
                      </div>
                      <button
                        type="button"
                        onClick={() => clearMaskedField(field.key)}
                        className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline"
                      >
                        Replace {field.label.toLowerCase()}
                      </button>
                    </div>
                  ) : (
                    <input
                      type={field.type === "password" ? "password" : "text"}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={value}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      placeholder={
                        field.placeholder || `Enter ${field.label}`
                      }
                    />
                  )}
                  {field.helpText && (
                    <p className="text-xs text-base-content/60 mt-1">
                      {field.helpText}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Enable toggle */}
        {selectedVendorDef && (
          <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
            <div>
              <h4 className="font-medium text-base-content">
                Enable Integration
              </h4>
              <p className="text-sm text-gray-600 dark:text-base-content/70">
                Activate the connection to {selectedVendorDef.name}
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-md border-2 border-gray-400 bg-gray-400 checked:border-blue-500 checked:bg-blue-500 [&:not(:checked)]:bg-gray-400 [&:after]:!bg-white"
              checked={enabled}
              onChange={() => setEnabled(!enabled)}
            />
          </div>
        )}

        {/* Last verified */}
        {existingIntegration?.last_verified_at && (
          <p className="text-xs text-base-content/60">
            Last verified:{" "}
            {new Date(existingIntegration.last_verified_at).toLocaleString()}
          </p>
        )}

        {/* Test Result */}
        {testResult && (
          <div
            className={`p-4 rounded-md ${
              testResult.success
                ? "bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900"
                : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900"
            }`}
          >
            {testResult.success ? (
              <span className="text-green-800 dark:text-green-400">
                &#10003; Connection test successful!
              </span>
            ) : (
              <span className="text-red-800 dark:text-red-400">
                &#10007; Test failed: {testResult.error}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {selectedVendorDef && (
          <div className="flex gap-3 pt-4 border-t border-base-300">
            {existingIntegration && (
              <button
                onClick={handleVerify}
                disabled={verifying}
                className="px-4 py-2 border border-gray-300 dark:border-base-300 rounded-md text-gray-700 dark:text-base-content/80 hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? "Testing..." : "Test Connection"}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save Configuration"}
            </button>
            {existingIntegration && (
              <>
                {confirmDelete ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-base-content/60">Are you sure?</span>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? "Removing..." : "Confirm"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-base-300 rounded-md text-gray-700 dark:text-base-content/80 hover:bg-base-200"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="px-4 py-2 border border-red-300 dark:border-red-900 rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 ml-auto"
                  >
                    Remove Integration
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationManager;
